import { FunctionComponent, useEffect, useState } from 'react';
import { Box, Button, FormField, Header, Input, Modal, SpaceBetween } from '@amzn/awsui-components-react';
import { Order } from '@amzn/ito-client';
import { getString } from 'common/ui-string-labels/ui-strings-utils';
import { buildOrderReferenceBadges } from 'common/utils/order-reference-badges';

export interface RecordReceivedQuantityModalProps {
    visible: boolean;
    order?: Order;
    isProcessing: boolean;
    onDismiss: () => void;
    /** Present only when more than one order is queued; shows a Skip button and advances without saving. */
    onSkip?: () => void;
    onConfirm: (receivedQuantity: number) => void;
    /** Shows "(current/total)" in the modal title; omit for the single-order flow. */
    progress?: { current: number; total: number };
}

/** Extracts the previously recorded receivedQuantity (if any) from the order's top-level details. */
function getExistingReceivedQuantity(order?: Order): string {
    const details = order?.details as { receivedQuantity?: number } | undefined;
    return details?.receivedQuantity !== undefined ? String(details.receivedQuantity) : '';
}

export const RecordReceivedQuantityModal: FunctionComponent<RecordReceivedQuantityModalProps> = ({
    visible, order, isProcessing, onDismiss, onSkip, onConfirm, progress,
}) => {
    const [value, setValue] = useState('');

    useEffect(() => {
        if (visible) {
            setValue(getExistingReceivedQuantity(order));
        }
    }, [visible, order]);

    const parsed = Number(value);
    const isValid = value.trim() !== '' && Number.isInteger(parsed) && parsed >= 0;

    const confirm = () => {
        if (isValid) {
            onConfirm(parsed);
        }
    };

    const orderedQuantity = order?.items?.[0]?.quantity;

    return (
        <Modal
            data-testid="received-quantity-modal"
            visible={visible}
            onDismiss={onDismiss}
            header={
                <Header
                    variant="h2"
                    description={<SpaceBetween direction="horizontal" size="xs">{buildOrderReferenceBadges(order)}</SpaceBetween>}
                >
                    {getString('bulkOrders.submittedOrders.receivedQuantityModalTitle')}
                    {progress ? ` (${progress.current}/${progress.total})` : ''}
                </Header>
            }
            footer={
                <Box float="right">
                    <SpaceBetween direction="horizontal" size="xs">
                        {onSkip && (
                            <Button data-testid="received-quantity-skip" variant="link" onClick={onSkip} disabled={isProcessing}>{getString('bulkOrders.submittedOrders.queueSkip')}</Button>
                        )}
                        <Button data-testid="received-quantity-cancel" variant="link" onClick={onDismiss} disabled={isProcessing}>{getString('common.preferences.cancelLabel')}</Button>
                        <Button data-testid="received-quantity-confirm" variant="primary" onClick={confirm} disabled={!isValid} loading={isProcessing}>
                            {getString('bulkOrders.submittedOrders.receivedQuantityConfirm')}
                        </Button>
                    </SpaceBetween>
                </Box>
            }
        >
            <SpaceBetween direction="vertical" size="s">
                {orderedQuantity !== undefined && (
                    <Box>{getString('bulkOrders.submittedOrders.receivedQuantityOrderedLabel', { quantity: String(orderedQuantity) })}</Box>
                )}
                <FormField label={getString('bulkOrders.submittedOrders.receivedQuantityFieldLabel')}>
                    <Input
                        data-testid="received-quantity-input"
                        type="number"
                        value={value}
                        onChange={({ detail }) => setValue(detail.value)}
                        disabled={isProcessing}
                    />
                </FormField>
            </SpaceBetween>
        </Modal>
    );
};
